import Button from '../Button';
import ErrorMessage from '../Form/ErrorMessage';
import FormGroup, { FormItem } from '../Form/FormGroup';
import FormTextTip from '../Form/FormTextTip';
import Signature from '../Form/Signature';
import Row from '../Grid/Row';
import InputRadio, { InputRadioGroup } from '../Input/InputRadio';
import InputText from '../Input/InputText';
import { PayInfoChangeResult } from '../Layer/PayInfoChangeLayerPopup';
import Select from '../Select';
import {
  NotiBox,
  NotiParagraph,
  NotiTitle,
  NotiItem,
  NotiList,
} from '../Text/TextNoti';
import KcpAccountCheck from '../ThirdParty/KcpAccountCheck';
import NaverPayForm, { NaverPayRef } from '../ThirdParty/NaverPayForm';
import { MySupportInfoControllerService } from '@/__generated__/FrontApi';
import IconArs from '@/assets/img/ico_ars@2x.png';
import LabelText from '@/components/Form/LabelText';
import { H4 } from '@/components/Titles';
import {
  PAYTYPE_REGULAR_AUTO_BANKING,
  PAYTYPE_REGULAR_NAVERPAY,
  PAYTYPE_REGULAR_CARD,
  getPaymentTypeNameToCode,
} from '@/helpers/PaymentHelper';
import {
  ENUM_SUPPORTER_TYPE,
  MEMBER_TYPES,
  TYPE_MEMBER_TYPES,
} from '@/helpers/SupportHelper';
import { FormButton, FormContainer } from '@/page-blocks/auth/AuthCommon';
import {
  DonateFormItem,
  InputRadioPledgePaymentDay,
  LabelFlex,
} from '@/page-blocks/donate/DonateCommon';
import { usePopupStore } from '@/stores/PopupStore';
import { useUserStore } from '@/stores/UserStore';
import { useCertificationStore } from '@/stores/CertificationStore';
import dayjs from 'dayjs';
import { graphql, useStaticQuery } from 'gatsby';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useLocation } from '@reach/router';
import {
  MyPymentMethodUpsertVo,
  MyPymentMethodVo,
} from '@/helpers/TypesHelper';
import InputNumber from '../Input/InputNumber';
import Col from '../Grid/Col';
import InputGroupCard, {
  InputGroupCardDash,
  InputGroupCardSlash,
} from '../Input/InputGroupCard';
import { mobileRegex, SF_MAX_NAME_LENGTH } from '@/helpers/ValidationHelper';

const H4Title = styled(H4)`
  font-size: 20px;
  padding-bottom: 16px;
`;
const DirectDebit = styled.div`
  dl {
    margin-bottom: 19px;

    &:last-child {
      margin-bottom: 0;
    }
    dt {
      margin-bottom: 13px;
    }
  }
`;

interface MyPayInformationProps {
  className?: string;
  payInfo: MyPymentMethodVo | undefined;
  pledgeInfo: any;
  supporterType: 'person' | 'group';
  memberTypeCode: keyof TYPE_MEMBER_TYPES;
  onSuccess: (result: PayInfoChangeResult) => void;
}

type SubmitParams = Parameters<
  typeof MySupportInfoControllerService.setMyPaymentMethodUsingPost
>[0];

const MyPayInformation: React.FC<MyPayInformationProps> = ({
  className,
  supporterType,
  memberTypeCode,
  payInfo,
  pledgeInfo,
  onSuccess,
}) => {
  const popupStore = usePopupStore();
  const userStore = useUserStore();
  const naverRef = useRef<NaverPayRef>(null);
  const {
    register,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    getValues,
    reset,
    trigger,
  } = useForm<MyPymentMethodUpsertVo>({
    defaultValues: {
      ...payInfo,
      ownerType: payInfo?.ownerType,
      paymentNumber: payInfo?.paymentNumber,
      paymentType:
        getPaymentTypeNameToCode(payInfo?.paymentType || '') ||
        PAYTYPE_REGULAR_AUTO_BANKING,
    },
  });
  const certificationStore = useCertificationStore();
  const cardNumberError =
    errors.cardNumber1 ||
    errors.cardNumber2 ||
    errors.cardNumber3 ||
    errors.cardNumber4;
  const cardExpireError = errors.cardExpireM || errors.cardExpireY;
  const {
    allBank: { nodes: banks },
  } = useStaticQuery(graphql`
    query {
      allBank {
        nodes {
          code
          description
        }
      }
    }
  `);

  const [isValidBankAccount, setIsValidBankAccount] = useState(false);
  const paymentType = watch(`paymentType`);
  const ownerType = watch(`ownerType`);
  const autoDebitConsent = watch(`autoDebitConsent`);
  const isOverduePage = useLocation().pathname.startsWith('/donate/overdue/')
    ? true
    : false;

  const getNumberOnly = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');
  };

  const onSubmit: SubmitHandler<MyPymentMethodUpsertVo> = useCallback(
    async (formData) => {
      const cpCode = sessionStorage.getItem(`cpCode`) as any;
      const solicitCode = sessionStorage.getItem(
        `or.kr.unicef.miss.info.solicitCode`,
      ) as any;

      if (isOverduePage !== true) {
        if (!userStore.user) {
          console.error(`사용자정보 없음`);
          return false;
        }
        console.log('userStore.user', userStore.user);
        if (
          formData.paymentType === PAYTYPE_REGULAR_CARD &&
          !userStore.user.email
        ) {
          popupStore.show(
            `개인정보 메뉴에서 이메일주소를 등록 후 시도해 주세요.`,
          );
          return false;
        }
        formData.webId = userStore.user.userId;
      } else {
        if (!certificationStore.user) {
          console.error(`인증 정보 없음`);
          return false;
        }
        if (
          formData.paymentType === PAYTYPE_REGULAR_CARD &&
          !certificationStore.user.email
        ) {
          popupStore.show(
            `개인정보 메뉴에서 이메일주소를 등록 후 시도해 주세요.`,
          );
          return;
        }
      }
      const params: SubmitParams = {
        pgType: 'KCP', // 기본 KCP
        pledgeId: pledgeInfo.pledgeId,
        webId: formData.webId,
        ownerType: formData.ownerType,
        ownerPhone: formData.ownerPhone,
        paymentType: formData.paymentType,
        paymentNumber:
          formData.paymentType === PAYTYPE_REGULAR_CARD
            ? `${formData.cardNumber1}${formData.cardNumber2}${formData.cardNumber3}${formData.cardNumber4}`
            : formData.paymentNumber,
        holderName: formData.holderName,
        companyName: formData.companyName,
        birthdate: formData.birthdate,
        cardExpiry: `${formData.cardExpireY}${formData.cardExpireM}`,
        naverpayKey: formData.naverpayKey,
        autoDebitConsent: formData.autoDebitConsent,
        digitalSignatureUrl: formData.digitalSignatureUrl,
        mandateId: formData.mandateId,
        relation: formData.relation,
        signBinary: formData.signBinary,
        dayOfMonth: formData.paymentDay,
        // 기타 정보
        pathType: isOverduePage ? 'MISSMODULE' : 'SUPPORT',
        cpCode,
        solicitCode,
      };

      try {
        const {
          resultCode,
          resultMessage,
          data,
        } = await MySupportInfoControllerService.setMyPaymentMethodUsingPost(
          params,
        );
        if (resultCode === `success`) {
          // 네이버 페이의 경우
          if (formData.paymentType === PAYTYPE_REGULAR_NAVERPAY) {
            if (!data.reserveId) {
              popupStore.show(`네이버페이 주요정보가 넘어오지 않았습니다`);
              return false;
            }
            naverRef.current?.payRecurrent(data);
            return false;
          }
          await onSuccess(data);
        } else {
          popupStore.show(resultMessage || `오류가 발생했습니다.`);
        }
      } catch (e: any) {
        if (e.response) {
          popupStore.show(
            e.response.data.resultMessage || `오류가 발생했습니다.`,
          );
        }
      }
    },
    [
      onSuccess,
      userStore.user,
      popupStore,
      certificationStore.user,
      getValues,
      ownerType,
      naverRef,
      setValue,
    ],
  );

  // 자동이체 & 개인/단체 선택에 따라 기본값 세팅
  useEffect(() => {
    if (paymentType === PAYTYPE_REGULAR_AUTO_BANKING) {
      if (ownerType === undefined || ownerType === null) {
        setValue(`ownerType`, 'Self');
      }
      if (ownerType === `Self`) {
        if (isOverduePage !== true) {
          setValue(`holderName`, userStore.user?.name || '');
          setValue(`birthdate`, userStore.getBirthday());
        } else {
          setValue(`holderName`, certificationStore.user?.name || '');
          setValue(`birthdate`, certificationStore.user?.birthDay || '');
        }
        setValue(`autoDebitConsent`, `sign`);
      }
      if (ownerType === `Other`) {
        setValue(`autoDebitConsent`, `ARS`);
      }
      // 계좌인증상태 초기화
      setIsValidBankAccount(false);
    }
  }, [userStore, certificationStore, paymentType, ownerType]);

  // 기본값 설정
  useEffect(() => {
    const defaultData = {
      signBinary: ``,
      paymentType: payInfo?.paymentType,
      autoDebitConsent: payInfo?.autoDebitConsent === `ARS` ? `ARS` : `sign`,
    };

    if (payInfo?.paymentType === PAYTYPE_REGULAR_AUTO_BANKING) {
      reset({
        ...defaultData,
        ownerType: payInfo.ownerType === `Other` ? `Other` : `Self`,
        relation: payInfo.relation,
        paymentNumber: '',
        ownerPhone: payInfo.ownerPhone,
        autoDebitConsent:
          payInfo.ownerType === `Other` ? `ARS` : defaultData.autoDebitConsent,
      });
    } else if (
      payInfo?.paymentType === PAYTYPE_REGULAR_CARD ||
      payInfo?.paymentType === PAYTYPE_REGULAR_NAVERPAY
    ) {
      reset({
        ...defaultData,
      });
    }
  }, [payInfo, reset]);

  return (
    <>
      <FormContainer
        className={`${className}`}
        css={`
          padding: 0;
        `}
      >
        <form name="frmPayMethodForm" onSubmit={handleSubmit(onSubmit)}>
          <FormItem>
            <FormGroup noMargin>
              <H4Title>결제수단</H4Title>
              <InputRadioGroup full>
                <InputRadio
                  name="paymentType"
                  ref={register({
                    required: {
                      value: true,
                      message: `필수 입력입니다`,
                    },
                  })}
                  tab
                  value={PAYTYPE_REGULAR_AUTO_BANKING}
                  label="자동이체"
                  defaultChecked
                  className={className}
                />
                <InputRadio
                  name="paymentType"
                  ref={register}
                  tab
                  value={PAYTYPE_REGULAR_CARD}
                  label="신용카드"
                  className={className}
                />
                <InputRadio
                  name="paymentType"
                  ref={register}
                  tab
                  value={PAYTYPE_REGULAR_NAVERPAY}
                  label="네이버페이(Npay)"
                  className={className}
                />
              </InputRadioGroup>
            </FormGroup>
          </FormItem>
          {paymentType === PAYTYPE_REGULAR_AUTO_BANKING && (
            <>
              <DonateFormItem>
                <FormGroup>
                  <H4>자동이체 계좌 정보</H4>
                  {/* 개인 선택의 경우 */}
                  <InputRadioGroup full>
                    <InputRadio
                      name="ownerType"
                      ref={register}
                      tab
                      value="Self"
                      label="본인"
                    />
                    <InputRadio
                      name="ownerType"
                      ref={register}
                      tab
                      value="Other"
                      label="타인"
                      onClick={() => {
                        setValue(`companyName`, ``);
                        setValue(`birthdate`, ``);
                      }}
                    />
                  </InputRadioGroup>
                </FormGroup>
                <FormGroup>
                  <LabelText require>예금주명</LabelText>
                  <InputText
                    name="holderName"
                    ref={register({
                      required: {
                        value: true,
                        message: `필수 입력입니다`,
                      },
                      maxLength: {
                        value: SF_MAX_NAME_LENGTH,
                        message: `예금주명은 ${SF_MAX_NAME_LENGTH}자 이하로 입력해주세요`,
                      },
                    })}
                    placeholder="예금주명 입력"
                    readOnly={ownerType === `Self` || isValidBankAccount}
                  />
                  {errors.holderName && (
                    <ErrorMessage>{errors.holderName.message}</ErrorMessage>
                  )}
                </FormGroup>
                {/* 개인의 경우 */}
                {supporterType === ENUM_SUPPORTER_TYPE.person ? (
                  <>
                    {ownerType === `Other` && (
                      <FormGroup>
                        <LabelText require>예금주와의 관계</LabelText>
                        <Select
                          name="relation"
                          ref={register({
                            required: {
                              value: true,
                              message: `필수 입력입니다`,
                            },
                          })}
                        >
                          <option value="">관계 선택</option>
                          <option value="Parent">부모</option>
                          <option value="Relative">친척</option>
                          <option value="Acquaintance">지인</option>
                          <option value="Teacher">선생님</option>
                          <option value="Other">기타</option>
                        </Select>
                        {errors.relation && (
                          <ErrorMessage>{errors.relation.message}</ErrorMessage>
                        )}
                      </FormGroup>
                    )}
                    <FormGroup>
                      <LabelText require>예금주 생년월일</LabelText>
                      <InputText
                        name="birthdate"
                        ref={register({
                          required: {
                            value: true,
                            message: `필수 입력입니다`,
                          },
                          validate: (value) =>
                            dayjs(value, `YYMMDD`, true).isValid() ||
                            `올바르지 않은 형식입니다`,
                        })}
                        placeholder="예금주 생년월일 6자리 입력(YYMMDD)"
                        readOnly={ownerType === `Self`}
                      />
                      {errors.birthdate && (
                        <ErrorMessage>{errors.birthdate.message}</ErrorMessage>
                      )}
                    </FormGroup>
                    {ownerType === `Other` && (
                      <FormGroup>
                        <LabelText require>예금주 휴대폰번호</LabelText>
                        <InputText
                          name="ownerPhone"
                          ref={register({
                            required: {
                              value: true,
                              message: `필수 입력입니다`,
                            },
                            pattern: {
                              value: mobileRegex,
                              message: `올바른 휴대폰번호가 아닙니다`,
                            },
                            validate: {
                              isValidRange: (value) =>
                                (value.substr(0, 3) === `010` &&
                                  value.length === 11) ||
                                (value.substr(0, 3) !== `010` &&
                                  value.length === 10) ||
                                `휴대폰번호 자릿수를 확인해주세요`,
                            },
                          })}
                          placeholder="예금주 휴대폰번호 입력 ( &lsquo;-&rsquo; 제외 )"
                        />
                        {errors.ownerPhone && (
                          <ErrorMessage>
                            {errors.ownerPhone.message}
                          </ErrorMessage>
                        )}
                      </FormGroup>
                    )}
                  </>
                ) : (
                  <>
                    {/* 법인/단체 경우 */}
                    {memberTypeCode &&
                    Object.keys(
                      MEMBER_TYPES[ENUM_SUPPORTER_TYPE.corporation],
                    ).includes(memberTypeCode) ? (
                      <FormGroup>
                        <LabelText require>예금주 사업자등록번호</LabelText>
                        <InputText
                          name="birthdate"
                          ref={register({
                            required: {
                              value: true,
                              message: `필수 입력입니다`,
                            },
                            validate: (value) =>
                              /^[0-9]{10}$/.test(value) ||
                              `사업자등록번호 10자리를 정확히 입력해주세요`,
                          })}
                          placeholder="예금주 사업자등록번호 10자리 입력"
                        />
                        {errors.birthdate && (
                          <ErrorMessage>
                            {errors.birthdate.message}
                          </ErrorMessage>
                        )}
                      </FormGroup>
                    ) : (
                      // 단체의 경우
                      <FormGroup>
                        <LabelText require>예금주 생년월일</LabelText>
                        <InputText
                          name="birthdate"
                          ref={register({
                            required: {
                              value: true,
                              message: `필수 입력입니다`,
                            },
                            validate: (value) =>
                              dayjs(value, `YYMMDD`, true).isValid() ||
                              `올바르지 않은 형식입니다`,
                          })}
                          placeholder="예금주 생년월일 6자리 입력(YYMMDD)"
                        />
                        {errors.birthdate && (
                          <ErrorMessage>
                            {errors.birthdate.message}
                          </ErrorMessage>
                        )}
                      </FormGroup>
                    )}
                  </>
                )}
                <FormGroup>
                  <LabelText require>계좌번호</LabelText>
                  <Select
                    name="companyName"
                    ref={register({
                      required: {
                        value: true,
                        message: `필수 입력입니다`,
                      },
                    })}
                    readOnly={isValidBankAccount}
                  >
                    <option value="">은행 선택</option>
                    {banks.map((bank: any) => (
                      <option key={bank.code} value={bank.code}>
                        {bank.description}
                      </option>
                    ))}
                  </Select>
                  {errors.companyName && (
                    <ErrorMessage>{errors.companyName.message}</ErrorMessage>
                  )}
                  <Row
                    className="certify"
                    css={`
                      margin-top: 8px;
                    `}
                  >
                    <div className="col-right">
                      <Controller
                        control={control}
                        name="paymentNumber"
                        rules={{
                          required: {
                            value: true,
                            message: `필수 입력입니다`,
                          },
                          validate: () =>
                            isValidBankAccount || `계좌인증은 필수입니다`,
                        }}
                        as={
                          <InputNumber
                            placeholder="계좌번호 입력(&lsquo;-&rsquo; 제외)"
                            readOnly={isValidBankAccount}
                            allowNegative={false}
                            allowLeadingZeros
                            onKeyUp={getNumberOnly}
                          />
                        }
                      />
                    </div>

                    <div className="col-left">
                      {isValidBankAccount ? (
                        <FormButton color="grey" disabled>
                          인증완료
                        </FormButton>
                      ) : (
                        <KcpAccountCheck
                          getParams={() => ({
                            name: getValues(`holderName`),
                            account: getValues(`paymentNumber`),
                            bankCode: getValues(`companyName`),
                          })}
                          renderSubmitButton={(buttonProps) => (
                            <FormButton outline {...buttonProps}>
                              계좌인증
                            </FormButton>
                          )}
                          onSuccess={() => {
                            setIsValidBankAccount(true);
                            setTimeout(() => {
                              trigger(`paymentNumber`);
                            }, 0);
                          }}
                          onBlocked={() => {
                            setValue(`paymentNumber`, ``);
                          }}
                          onError={(message) => popupStore.show(message)}
                        />
                      )}
                    </div>
                  </Row>
                  {errors.paymentNumber && (
                    <ErrorMessage>{errors.paymentNumber.message}</ErrorMessage>
                  )}
                  <Row
                    className="certify"
                    css={`
                      margin-top: 8px;
                      align-items: center;
                    `}
                  >
                    <div className="col-right">
                      <FormTextTip>
                        <strong>
                          가상 계좌, 미성년자 명의 계좌, 기업은행 휴대폰 계좌는
                          사용하실 수 없습니다.
                        </strong>
                      </FormTextTip>
                    </div>
                  </Row>
                </FormGroup>
              </DonateFormItem>
              <DonateFormItem noBorder>
                <FormGroup noMargin>
                  <H4>결제일</H4>
                  <InputRadioPledgePaymentDay
                    name="dayOfMonth"
                    paytype={PAYTYPE_REGULAR_AUTO_BANKING}
                    ref={register({
                      required: {
                        value: true,
                        message: `필수 입력입니다`,
                      },
                    })}
                    className="donate"
                  />
                  {errors.dayOfMonth && (
                    <ErrorMessage>{errors.dayOfMonth.message}</ErrorMessage>
                  )}
                </FormGroup>
              </DonateFormItem>
              <DonateFormItem noBorder>
                <FormGroup>
                  <H4>자동이체 출금 동의</H4>
                  <DirectDebit>
                    {ownerType === `Self` && (
                      <dl>
                        <dt>
                          <InputRadio
                            name="autoDebitConsent"
                            ref={register({
                              required: {
                                value: true,
                                message: `필수 입력입니다`,
                              },
                            })}
                            value="sign"
                            label="전자서명"
                          />
                        </dt>
                        {autoDebitConsent === `sign` && (
                          <dd>
                            <Controller
                              control={control}
                              name="signBinary"
                              rules={{
                                required: {
                                  value: true,
                                  message: `필수 입력입니다`,
                                },
                              }}
                              render={(renderProps) => (
                                <Signature {...renderProps} />
                              )}
                            />
                            {errors.signBinary && (
                              <ErrorMessage>
                                {errors.signBinary.message}
                              </ErrorMessage>
                            )}
                          </dd>
                        )}
                      </dl>
                    )}
                    <dl>
                      <dt>
                        <InputRadio
                          name="autoDebitConsent"
                          ref={register({
                            required: {
                              value: true,
                              message: `필수 입력입니다`,
                            },
                          })}
                          value="ARS"
                          label="ARS"
                        />
                      </dt>
                      {autoDebitConsent === `ARS` && (
                        <dd>
                          <NotiBox>
                            <NotiParagraph>
                              금융결제원의 방침에 따라 개인정보 보호를 위해 잠시
                              후 ARS전화로 자동이체 출금동의를 진행합니다.
                            </NotiParagraph>
                            <NotiParagraph>
                              수신거부 또는 입력하신 휴대폰번호가 다를 경우
                              출금동의여부 확인이 어려우니 아래 번호의 전화를 꼭
                              수신해 주시기 바랍니다.
                            </NotiParagraph>
                            <a
                              href="tel:027238215"
                              css={`
                                font-size: 24px;
                                font-weight: bold;
                                line-height: 1.58;
                                letter-spacing: -1.2px;
                                margin-top: 24px;
                                display: block;
                                color: #1cabe2;
                                text-decoration: underline;
                              `}
                            >
                              <img
                                src={IconArs}
                                alt="ARS 아이콘"
                                css={`
                                  display: inline-block;
                                  width: 40px;
                                  margin-right: 16px;
                                  vertical-align: middle;
                                  border-radius: 100%;
                                `}
                              />
                              02 - 723 - 8215
                            </a>
                          </NotiBox>
                        </dd>
                      )}
                    </dl>
                  </DirectDebit>
                  {errors.autoDebitConsent && (
                    <ErrorMessage>
                      {errors.autoDebitConsent.message}
                    </ErrorMessage>
                  )}
                </FormGroup>
                <NotiBox>
                  <NotiTitle>자동이체 후원 시 유의사항</NotiTitle>
                  <NotiParagraph>
                    <NotiItem>
                      후원 신청 시, 첫 회 후원금은 신청하신 당일에 출금됩니다.
                      다음 후원부터는 매월 지정하신 날짜에 진행됩니다.
                    </NotiItem>
                    <NotiItem>
                      매월 선택하신 날짜에 약정하신 금액이 자동 인출 또는
                      승인됩니다.
                    </NotiItem>
                  </NotiParagraph>
                </NotiBox>
              </DonateFormItem>
            </>
          )}
          {paymentType === PAYTYPE_REGULAR_CARD && (
            <>
              <DonateFormItem>
                <H4>카드번호</H4>
                <Col
                  css={`
                    margin-top: 8px;
                  `}
                >
                  <InputGroupCard>
                    <Controller
                      control={control}
                      name="cardNumber1"
                      rules={{
                        required: {
                          value: true,
                          message: `필수 입력입니다`,
                        },
                        maxLength: 4,
                      }}
                      maxLength={4}
                      as={
                        <InputText placeholder="0000" onKeyUp={getNumberOnly} />
                      }
                    />
                    <InputGroupCardDash />
                    <Controller
                      control={control}
                      name="cardNumber2"
                      rules={{
                        required: {
                          value: true,
                          message: `필수 입력입니다`,
                        },
                        maxLength: 4,
                      }}
                      maxLength={4}
                      as={
                        <InputText placeholder="0000" onKeyUp={getNumberOnly} />
                      }
                    />
                    <InputGroupCardDash />
                    <Controller
                      control={control}
                      name="cardNumber3"
                      rules={{
                        required: {
                          value: true,
                          message: `필수 입력입니다`,
                        },
                        maxLength: 4,
                      }}
                      maxLength={4}
                      as={
                        <InputText placeholder="0000" onKeyUp={getNumberOnly} />
                      }
                    />
                    <InputGroupCardDash />
                    <Controller
                      control={control}
                      name="cardNumber4"
                      rules={{
                        required: {
                          value: true,
                          message: `필수 입력입니다`,
                        },
                        maxLength: 4,
                      }}
                      maxLength={4}
                      as={
                        <InputText placeholder="0000" onKeyUp={getNumberOnly} />
                      }
                    />
                  </InputGroupCard>
                </Col>
                {cardNumberError && (
                  <ErrorMessage>{cardNumberError.message}</ErrorMessage>
                )}
              </DonateFormItem>
              <DonateFormItem>
                <H4>유효기간</H4>
                <InputGroupCard>
                  <Controller
                    control={control}
                    name="cardExpireM"
                    rules={{
                      required: {
                        value: true,
                        message: `필수 입력입니다`,
                      },
                      validate: (value) => {
                        const m = parseInt(value, 10);
                        if (isNaN(m) || m < 1 || m > 12)
                          return `올바른 월을 입력해주세요 (01~12)`;
                        return true;
                      },
                    }}
                    as={<InputNumber format="##" placeholder="MM" />}
                  />
                  <InputGroupCardSlash />
                  <Controller
                    control={control}
                    name="cardExpireY"
                    rules={{
                      required: {
                        value: true,
                        message: `필수 입력입니다`,
                      },
                    }}
                    as={<InputNumber format="##" placeholder="YY" />}
                  />
                </InputGroupCard>
                {cardExpireError && (
                  <ErrorMessage>{cardExpireError.message}</ErrorMessage>
                )}
              </DonateFormItem>
              {Object.keys(
                MEMBER_TYPES[ENUM_SUPPORTER_TYPE.corporation],
              ).includes(memberTypeCode || ``) ? (
                // 법인의 경우
                <DonateFormItem>
                  <H4>카드 소유주 사업자번호</H4>
                  <Controller
                    control={control}
                    name="birthdate"
                    rules={{
                      required: {
                        value: true,
                        message: `필수 입력입니다`,
                      },
                      validate: (value) =>
                        /^[0-9]{10}$/.test(value) ||
                        `사업자번호 10자리를 정확히 입력해주세요`,
                    }}
                    as={
                      <InputNumber
                        format="##########"
                        placeholder="사업자번호 입력(&lsquo;-&rsquo; 제외)"
                        css={`
                          margin-top: 12px;
                        `}
                      />
                    }
                  />
                  {errors.birthdate && (
                    <ErrorMessage>{errors.birthdate.message}</ErrorMessage>
                  )}
                </DonateFormItem>
              ) : (
                // 개인, 단체의 경우
                <DonateFormItem>
                  <H4>카드 소유주 생년월일</H4>
                  {useLocation().pathname.indexOf('/overdue') < 0 ? (
                    <Controller
                      control={control}
                      name="birthdate"
                      rules={{
                        required: {
                          value: true,
                          message: `필수 입력입니다`,
                        },
                        validate: (value) =>
                          dayjs(value, `YYMMDD`, true).isValid() ||
                          `올바르지 않은 형식입니다`,
                      }}
                      as={
                        <InputNumber
                          format="######"
                          placeholder="생년월일 6자리 입력(YYMMDD)"
                          css={`
                            margin-top: 12px;
                          `}
                        />
                      }
                    />
                  ) : (
                    <Controller
                      control={control}
                      name="birthdate"
                      rules={{
                        required: {
                          value: true,
                          message: `필수 입력입니다`,
                        },
                        maxLength: 6,
                        validate: (value) =>
                          dayjs(value, `YYMMDD`, true).isValid() ||
                          `올바르지 않은 형식입니다`,
                      }}
                      as={
                        <InputText
                          placeholder="생년월일 6자리 입력(YYMMDD)"
                          css={`
                            margin-top: 12px;
                          `}
                          onKeyUp={getNumberOnly}
                        />
                      }
                    />
                  )}

                  {errors.birthdate && (
                    <ErrorMessage>{errors.birthdate.message}</ErrorMessage>
                  )}
                </DonateFormItem>
              )}
              <DonateFormItem noBorder>
                <FormGroup noMargin>
                  <H4>결제일</H4>
                  <InputRadioPledgePaymentDay
                    name="dayOfMonth"
                    paytype={PAYTYPE_REGULAR_CARD}
                    ref={register({
                      required: {
                        value: true,
                        message: `필수 입력입니다`,
                      },
                    })}
                    className="donate"
                  />
                  {errors.dayOfMonth && (
                    <ErrorMessage>{errors.dayOfMonth.message}</ErrorMessage>
                  )}
                </FormGroup>
              </DonateFormItem>
              <DonateFormItem>
                <NotiBox
                  css={`
                    margin-top: 8px;
                  `}
                >
                  <NotiTitle>신용카드 후원 시 유의사항</NotiTitle>
                  <NotiList>
                    <NotiItem>
                      후원 신청 시, 첫 회 후원금은 신청하신 당일에 출금됩니다.
                      다음 후원부터는 매월 지정하신 날짜에 진행됩니다.
                    </NotiItem>
                    <NotiItem>
                      매월 선택하신 날짜에 약정하신 금액이 자동 인출 또는
                      승인됩니다.
                    </NotiItem>
                  </NotiList>
                </NotiBox>
              </DonateFormItem>
            </>
          )}
          {paymentType === PAYTYPE_REGULAR_NAVERPAY && (
            <>
              <DonateFormItem noBorder>
                <FormGroup noMargin>
                  <H4>결제일</H4>
                  <InputRadioPledgePaymentDay
                    name="dayOfMonth"
                    paytype={PAYTYPE_REGULAR_NAVERPAY}
                    ref={register({
                      required: {
                        value: true,
                        message: `필수 입력입니다`,
                      },
                    })}
                    className="donate"
                  />
                  {errors.dayOfMonth && (
                    <ErrorMessage>{errors.dayOfMonth.message}</ErrorMessage>
                  )}
                </FormGroup>
              </DonateFormItem>
              <DonateFormItem>
                <NotiBox
                  css={`
                    margin-top: 8px;
                  `}
                >
                  <NotiTitle>네이버페이(Npay) 후원 시 유의사항</NotiTitle>
                  <NotiList>
                    <NotiItem>
                      후원 신청 시, 첫 회 후원금은 신청하신 당일에 출금됩니다.
                      다음 후원부터는 매월 지정하신 날짜에 진행됩니다.
                    </NotiItem>
                    <NotiItem>
                      매월 선택하신 날짜에 약정하신 금액이 자동 인출 또는
                      승인됩니다.
                    </NotiItem>
                  </NotiList>
                </NotiBox>
              </DonateFormItem>
            </>
          )}

          <Button
            type="submit"
            full
            css={`
              margin: 92px 0;
            `}
            disabled={isSubmitting}
          >
            등록하기
          </Button>
        </form>
      </FormContainer>
      <NaverPayForm ref={naverRef} />
    </>
  );
};
export default MyPayInformation;
